import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { PageHero, SectionHead, Reveal } from "@/components/Shared";
import ImpactGrid from "@/components/ImpactGrid";

const LEVELS = ["United States", "Florida", "Jacksonville", "Neighborhood / ZIP", "Swim for Rakim Projects"];

function StatCard({ s, i }) {
  return (
    <Reveal delay={(i % 4) * 0.06}>
      <div className={`rounded-2xl p-6 h-full flex flex-col ${s.featured ? "bg-[#03045E] text-white" : "bg-white border border-sky-100"}`} data-testid={`stat-card-${s.key}`}>
        <p className={`font-display text-5xl font-bold ${s.featured ? "text-white" : "text-[#03045E]"}`}>{s.value}</p>
        <p className={`mt-2 font-semibold ${s.featured ? "text-sky-100" : "text-slate-700"}`}>{s.label}</p>
        <dl className={`mt-5 pt-4 border-t text-xs grid grid-cols-[90px_1fr] gap-y-1.5 ${s.featured ? "border-white/15 text-sky-200" : "border-slate-100 text-slate-500"}`}>
          <dt className="font-bold uppercase tracking-wider">Source</dt><dd><a href={s.source_url} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">{s.source} <ExternalLink className="w-3 h-3" /></a></dd>
          <dt className="font-bold uppercase tracking-wider">Year</dt><dd>{s.data_year}</dd>
          <dt className="font-bold uppercase tracking-wider">Updated</dt><dd>{s.last_updated}</dd>
          <dt className="font-bold uppercase tracking-wider">Definition</dt><dd>{s.definition}</dd>
        </dl>
      </div>
    </Reveal>
  );
}

export default function DataCenter() {
  const { data = [] } = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/stats").then((r) => r.data) });
  return (
    <div data-testid="data-center-page">
      <PageHero eyebrow="Drowning Data Center™" title="Authoritative numbers. Clearly sourced." sub="Every government statistic here shows its source, data period, last update and definition — so older figures are never mistaken for current ones. These are annual estimates, not a live counter." />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">{data.map((s, i) => <StatCard key={s.key} s={s} i={i} />)}</div>
      </section>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-14">
        <SectionHead eyebrow="Coming next" title="National → State → City → ZIP" sub="State, city and neighborhood views will be added as reliable official data sets are connected. Until then, only national CDC figures are shown." />
        <div className="flex flex-wrap items-center gap-2 mt-6" data-testid="data-drilldown">
          {LEVELS.map((l, i) => (
            <span key={l} className="flex items-center gap-2">
              <span className={`rounded-full px-4 py-2 text-sm font-bold ${i === 0 ? "bg-[#03045E] text-white" : "bg-white border border-sky-200 text-slate-500"}`}>{l}{i > 0 && i < 4 ? " — soon" : ""}</span>
              {i < LEVELS.length - 1 && <span className="text-sky-400">→</span>}
            </span>
          ))}
        </div>
      </section>
      <section className="bg-white py-14">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead eyebrow="Swim for Rakim projects" title="Our program data" />
          <div className="mt-8"><ImpactGrid /></div>
        </div>
      </section>
    </div>
  );
}
